import type {CSSProperties} from 'react';
import {WEDGE_BRIEF_FLOAT as F} from '@territory/tokens';
import {wedgeExactRole as r} from './wedgeRoles.js';

export type WedgeBriefFloatLook = 'brieffloat-dismiss' | 'brieffloat-outcome' | 'brieffloat-novisit' | 'brieffloat-selpill' | 'brieffloat-chip' | 'brieffloat-chip-on' | 'brieffloat-toc' | 'brieffloat-toc-on';

export const isWedgeBriefFloatLook = (look: string | undefined): look is WedgeBriefFloatLook => look !== undefined && look.startsWith('brieffloat-');

/* Released floating chrome of a brief. Dismiss is the 30px bare box in the corner of the walk-in ask card. Outcome (Won, Pushed, Lost) is a
   flex 1 tile on the surface fill with a hairline edge and 40px tall at least; No visit is the bare flex 1.2 sibling. The selection pill
   (Add selection to notes) is a 44px glass pill on the scrim wash with a blur, the bold edge and the fixed drop shadow, and it takes taps
   itself because its fixed wrapper does not. A chapter chip is a bare round chip that takes the accent fill and the fixed white ink while
   its chapter is on screen. A desktop contents row is a bare 8px row that takes the accent wash, the accent ink and a 2px accent bar on
   its left edge while its section is on screen. Each keeps the declaration order of the source so the shipped ink rules keep matching the
   serialized text. None draws a transparent target or an anchor: legacy geometry holds and the pills sit apart from other controls. */
export function wedgeBriefFloatStyle(look: WedgeBriefFloatLook): CSSProperties {
  switch (look) {
    case 'brieffloat-dismiss': return {
      width: 30, height: 30, flexShrink: 0, background: 'transparent', border: 0, cursor: 'pointer', display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: r('labelTer'), WebkitTapHighlightColor: 'transparent',
    };
    case 'brieffloat-outcome': return {
      flex: 1, padding: '9px 0', minHeight: 40, background: r('surface'), border: `1px solid ${r('cardBorder')}`, borderRadius: 9,
      color: r('label'), fontSize: 12.5, fontWeight: 700, fontFamily: r('font'), cursor: 'pointer', textTransform: 'capitalize',
      touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
    };
    case 'brieffloat-novisit': return {
      flex: 1.2, padding: '9px 0', minHeight: 40, background: 'transparent', border: 0, borderRadius: 9, color: r('labelSec'), fontSize: 12.5,
      fontWeight: 600, fontFamily: r('font'), cursor: 'pointer', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
    };
    case 'brieffloat-selpill': return {
      display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 16px', minHeight: 44, borderRadius: 999, background: r('scrim'),
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: `0.5px solid ${r('separatorBold')}`, boxShadow: F.selShadow,
      color: r('accent'), cursor: 'pointer', fontFamily: r('font'), fontSize: 13, fontWeight: 700, touchAction: 'manipulation',
      WebkitTapHighlightColor: 'transparent', pointerEvents: 'auto',
    };
    case 'brieffloat-chip': case 'brieffloat-chip-on': {
      const on = look === 'brieffloat-chip-on';
      return {
        border: 0, cursor: 'pointer', borderRadius: 999, padding: '7px 12px', flexShrink: 0, whiteSpace: 'nowrap', fontFamily: r('font'),
        fontSize: 12.5, fontWeight: 700, letterSpacing: 0.2, background: on ? r('accent') : 'transparent', color: on ? F.chapterInk : r('labelSec'),
      };
    }
    case 'brieffloat-toc': case 'brieffloat-toc-on': {
      const on = look === 'brieffloat-toc-on';
      return {
        background: on ? r('accentTint') : 'transparent', border: 0, cursor: 'pointer', padding: '8px 10px', borderRadius: 8, fontFamily: r('font'),
        fontSize: 13, fontWeight: on ? 700 : 500, color: on ? r('accent') : r('labelSec'), textAlign: 'left', letterSpacing: -0.1, display: 'flex',
        alignItems: 'center', gap: 8, borderLeft: on ? `2px solid ${r('accent')}` : '2px solid transparent',
        transition: `background 160ms ${r('ease')}, color 160ms ${r('ease')}, border-color 160ms ${r('ease')}`,
      };
    }
  }
}
